import React from 'react'
import clsx from 'clsx'
import { Maximize } from 'iconsax-react'
import { useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import { PinnedTab, selectPinnedTabs, addTab } from '@SonarCX/slices/pinnedTabs'

import ButtonSecondary from '../ButtonSecondary'
import Tooltip from '../Tooltip'

import useStyles from './styles'

type PinButtonProps = {
  id: string
  type: PinnedTab['type']
  title: PinnedTab['title']
  useButton?: boolean
  extra?: Record<string, any>
  onPin?: () => void
  className?: string
}

const PinButton: React.FC<PinButtonProps> = ({
  id,
  type,
  title,
  useButton,
  extra = {},
  onPin,
  className
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { tabs } = useAppSelector(selectPinnedTabs)

  const classes = useStyles()

  const disabled = React.useMemo(
    () => tabs.some(tab => tab.id === id),
    [tabs, id]
  )

  const WrapperElem = React.useMemo(
    () => (useButton ? ButtonSecondary : React.Fragment),
    [useButton]
  )

  const handleClick = React.useCallback(() => {
    if (disabled) {
      return
    }

    dispatch(addTab({ id, type, title, ...extra }))
    onPin && onPin()
  }, [disabled, dispatch, id, type, title, extra, onPin])

  return (
    <Tooltip title={disabled ? 'Already pinned' : 'Pin to bottom'}>
      <div
        onClick={handleClick}
        className={clsx(classes.root, disabled && classes.disabled, className)}
      >
        <WrapperElem>
          <Maximize
            size={theme.size.base}
            color={
              disabled
                ? theme.palette.secondary.main
                : theme.palette.primary.main
            }
            className={classes.icon}
          />
        </WrapperElem>
      </div>
    </Tooltip>
  )
}

export default PinButton
